import React, { useState, useEffect } from 'react';
import apiClient from '../../utils/api';

interface Competition {
  id: string;
  name: string;
  country: string;
  tier: number;
  isActive?: boolean;
}

interface Season {
  id: string;
  name: string;
  description: string;
  startDate: string;
  endDate: string;
  isActive: boolean;
  league?: Competition;
  createdAt: string;
  creator?: {
    id: string;
    name: string;
  };
}

const AdminSeasons: React.FC = () => {
  const [seasons, setSeasons] = useState<Season[]>([]);
  const [competitions, setCompetitions] = useState<Competition[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingSeason, setEditingSeason] = useState<Season | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    startDate: '',
    endDate: '',
    competitionId: '',
    isActive: true
  });

  useEffect(() => {
    fetchSeasons();
    fetchCompetitions();
  }, []);

  const fetchSeasons = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/admin/seasons');
      setSeasons(response.data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch seasons');
    } finally {
      setLoading(false);
    }
  };

  const fetchCompetitions = async () => {
    try {
      const response = await apiClient.get('/admin/competitions');
      setCompetitions(response.data.filter((competition: Competition) => competition.isActive !== false));
    } catch (err: any) {
      console.error('Failed to fetch competitions:', err);
    }
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await apiClient.post('/admin/seasons', formData);
      setShowCreateModal(false);
      setFormData({ name: '', description: '', startDate: '', endDate: '', competitionId: '', isActive: true });
      fetchSeasons();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to create season');
    }
  };

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingSeason) return;
    
    try {
      await apiClient.put(`/admin/seasons/${editingSeason.id}`, formData);
      setEditingSeason(null);
      setFormData({ name: '', description: '', startDate: '', endDate: '', competitionId: '', isActive: true });
      setShowCreateModal(false);
      fetchSeasons();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update season');
    }
  };

  const handleDelete = async (seasonId: string) => {
    if (!window.confirm('Are you sure you want to delete this season? This will also delete all related matches and data.')) {
      return;
    }

    try {
      await apiClient.delete(`/admin/seasons/${seasonId}`);
      fetchSeasons();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete season');
    }
  };

  const handleEdit = (season: Season) => {
    setEditingSeason(season);
    setFormData({
      name: season.name,
      description: season.description || '',
      startDate: season.startDate.split('T')[0],
      endDate: season.endDate.split('T')[0],
      competitionId: season.league?.id || '',
      isActive: season.isActive
    });
    setShowCreateModal(true);
  };

  const resetForm = () => {
    setFormData({ name: '', description: '', startDate: '', endDate: '', competitionId: '', isActive: true });
    setEditingSeason(null);
    setShowCreateModal(false);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-xl">Loading seasons...</div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold" style={{ color: 'var(--text-primary)' }}>
          Manage Seasons
        </h1>
        <button
          onClick={() => setShowCreateModal(true)}
          className="btn-primary px-4 py-2"
        >
          Create New Season
        </button>
      </div>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
          <button 
            onClick={() => setError('')}
            className="float-right text-red-700 hover:text-red-900"
          >
            ×
          </button>
        </div>
      )}

      <div className="grid gap-4">
        {seasons.map((season) => (
          <div key={season.id} className="card p-6">
            <div className="flex justify-between items-start">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <h3 className="text-xl font-semibold" style={{ color: 'var(--text-primary)' }}>
                    {season.name}
                  </h3>
                  <span className={`px-2 py-1 rounded text-xs font-medium ${
                    season.isActive 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-gray-100 text-gray-800'
                  }`}>
                    {season.isActive ? 'Active' : 'Inactive'}
                  </span>
                </div>
                
                <div className="mb-3">
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                    🏆 {season.league?.name || 'No Competition'}
                  </span>
                  {season.league && (
                    <span className="ml-2 text-sm" style={{ color: 'var(--text-secondary)' }}>
                      ({season.league.country} - Tier {season.league.tier})
                    </span>
                  )}
                </div>

                <div className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                  <div className="flex flex-wrap gap-4">
                    <span>📅 Start: {formatDate(season.startDate)}</span>
                    <span>🏁 End: {formatDate(season.endDate)}</span>
                    <span>👤 Created by: {season.creator?.name || 'Unknown'}</span>
                    <span>📝 Created: {formatDate(season.createdAt)}</span>
                  </div>
                </div>
              </div>
              
              <div className="flex gap-2">
                <button
                  onClick={() => handleEdit(season)}
                  className="btn-secondary px-3 py-1 text-sm"
                >
                  Edit
                </button>
                <button
                  onClick={() => handleDelete(season.id)}
                  className="bg-red-600 text-white px-3 py-1 rounded text-sm hover:bg-red-700"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        ))}

        {seasons.length === 0 && (
          <div className="text-center py-8">
            <p className="text-xl" style={{ color: 'var(--text-secondary)' }}>
              No seasons found. Create your first season!
            </p>
            <p className="text-sm mt-2" style={{ color: 'var(--text-secondary)' }}>
              Make sure to create competitions first, then create seasons for those competitions.
            </p>
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="card p-6 max-w-md w-full mx-4">
            <h2 className="text-xl font-bold mb-4" style={{ color: 'var(--text-primary)' }}>
              {editingSeason ? 'Edit Season' : 'Create New Season'}
            </h2>
            
            <form onSubmit={editingSeason ? handleUpdate : handleCreate}>
              <div className="mb-4">
                <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-primary)' }}>
                  Season Name *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-highlight focus:border-highlight"
                  required
                  placeholder="e.g., Premier League 2025/26"
                />
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-primary)' }}>
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-highlight focus:border-highlight h-20 resize-none"
                  placeholder="Brief description of the season..."
                />
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-primary)' }}>
                  Competition *
                </label>
                <select
                  value={formData.competitionId}
                  onChange={(e) => setFormData({ ...formData, competitionId: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-highlight focus:border-highlight"
                  required
                >
                  <option value="">Select a competition...</option>
                  {competitions.map((competition) => (
                    <option key={competition.id} value={competition.id}>
                      {competition.name} ({competition.country})
                    </option>
                  ))}
                </select>
                {competitions.length === 0 && (
                  <p className="text-sm text-red-600 mt-1">
                    No competitions available. Please create a competition first.
                  </p>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-primary)' }}>
                    Start Date *
                  </label>
                  <input
                    type="date"
                    value={formData.startDate}
                    onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-highlight focus:border-highlight"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-primary)' }}>
                    End Date *
                  </label>
                  <input
                    type="date"
                    value={formData.endDate}
                    onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-highlight focus:border-highlight"
                    required
                  />
                </div>
              </div>

              <div className="mb-6">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.isActive}
                    onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                    className="mr-2"
                  />
                  <span className="text-sm" style={{ color: 'var(--text-primary)' }}>
                    Season is active
                  </span>
                </label>
              </div>

              <div className="flex gap-3">
                <button
                  type="submit"
                  className="btn-primary flex-1"
                  disabled={competitions.length === 0}
                >
                  {editingSeason ? 'Update Season' : 'Create Season'}
                </button>
                <button
                  type="button"
                  onClick={resetForm}
                  className="btn-secondary flex-1"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminSeasons; 